import React from "react";
import * as Yup from "yup";
import { useParams } from "react-router-dom";
import { Row, Col, Card, Label } from "reactstrap";
import { InputText } from "primereact/inputtext";
import EditorContainer from "../../components/EditorContainer";
import { Dropdown } from "primereact/dropdown";
import Can, { canUserSave } from "../../auth/Can";
import roles from "../../auth/roles";
import { createProduct, updateProduct, getProduct } from "../../api/products";
import { getTenant } from "../../api/tenants";
import { useAuth0 } from "../../auth/react-auth0-wrapper";

const schema = () =>
    Yup.object({
        attributes: Yup.object({
            type: Yup.string()
                .required("Il tipo di dispositivo é obbligatorio")
                .nullable(),
            model: Yup.string()
                .required("Il modello di dispositivo é obbligatorio")
                .nullable(),
            supplier: Yup.string()
                .required("Il fornitore é obbligatorio")
                .nullable(),
            description: Yup.string()
                .required("La descrizione é obbligatoria")
                .nullable(),
            duration: Yup.number("La durata in mesi è obbligatoria")
                .required("La durata in mesi è obbligatoria")
                .nullable("La durata in mesi è obbligatoria").typeError('La durata in mesi è obbligatoria'),
            price: Yup.number("Il prezzo è obbligatorio")
                .required("Il prezzo è obbligatorio")
                .nullable("Il prezzo è obbligatorio").typeError('Il prezzo è obbligatorioè'),
            needsAuto: Yup.bool().nullable(),
            quotaAgent: Yup.string()
                .required("La quota agente é obbligatoria")
                .nullable()
        })
    });

const ProductEditor = ({ activeTenant }) => {

    const { id } = useParams();
    const { domains, user } = useAuth0();

    const [tenant, setTenant] = React.useState(activeTenant);
    const [readOnly, setReadOnly] = React.useState(readOnly);

    const onCreate = async entity => {
        const result = await createProduct({
            ...entity,
            tenant_id: activeTenant._id
        });
        return result;
    };

    const onLoad = async id => {
        const product = await getProduct(id);
        const tenantDett = await getTenant(product.tenant_id);
        setTenant(tenantDett);
        setReadOnly(!user.isInRole([roles.ADMIN]));
        return product;
    };

    const onUpdate = async entity => {
        const result = await updateProduct(entity);
        return result;
    };

    const onAttributesChange = (
        changedProps,
        attributes,
        containerHandleChange
    ) => {
        const newAttributes = { ...attributes, ...changedProps };
        containerHandleChange("attributes", { target: { value: newAttributes } });
    };

    return (
        <EditorContainer
            key={id ? `${id}-product` : "new-product"}
            canSave={canUserSave([roles.ADMIN], activeTenant)}
            schema={schema}
            onCreate={onCreate}
            onUpdate={onUpdate}
            onLoad={onLoad}
            optionalNav={{ link: `/tenant/products`, text: "Prodotti" }}
            redirRoot={`/tenant/products`}
            title={el =>
                `${id ? el.attributes.description : "NUOVO PRODOTTO"}`
            }
            children={(frmProps, containerHandleChange) => {
                const attributes = frmProps.values["attributes"] || {
                    model: "",
                    type: "",
                    supplier: "",
                    description: "",
                    price: "",
                    duration: "",
                    quotaAgent: "",
                    needsAuto: false
                };

                const attributesErrors = frmProps.errors.attributes || {};
                return (
                    <Row>
                        <Col sm="6" xs="12">
                            <strong className="mt-2">Descrizione Prodotto</strong>
                            <InputText
                                disabled={readOnly ? true : false}
                                className={attributesErrors.description ? "p-error" : ""}
                                value={attributes.description || ""}
                                style={{ width: "100%" }}
                                onChange={e =>
                                    onAttributesChange(
                                        { description: e.target.value },
                                        attributes,
                                        containerHandleChange
                                    )
                                }
                            />
                            {attributesErrors.description ? (<div className="invalid-feedback">{attributesErrors.description}</div>) : null}
                        </Col>
                        <Col sm="6" xs="12">
                            <strong className="mt-2">Fornitore</strong>
                            <Dropdown
                                filter
                                disabled={readOnly ? true : false}
                                filterBy="label"
                                className={attributesErrors.supplier ? "p-error" : ""}
                                value={attributes.supplier || ""}
                                showClear={true}
                                style={{ width: "100%" }}
                                options={domains.suppliers}
                                onChange={e =>
                                    onAttributesChange(
                                        { supplier: e.value },
                                        attributes,
                                        containerHandleChange
                                    )
                                }
                            />
                            {attributesErrors.supplier ? (<div className="invalid-feedback">{attributesErrors.supplier}</div>) : null}
                        </Col>
                        <Col sm="6" xs="12">
                            <strong className="mt-2">Tipo Prodotto</strong>
                            <Dropdown
                                disabled={readOnly ? true : false}
                                filter
                                filterBy="label"
                                id="type"
                                className={attributesErrors.type ? "p-error" : ""}
                                value={attributes.type || ""}
                                showClear={true}
                                style={{ width: "100%" }}
                                options={domains.devicesType}
                                onChange={e =>
                                    onAttributesChange({ type: e.value }, attributes, containerHandleChange)
                                }
                            />
                            {attributesErrors.type ? (<div className="invalid-feedback">{attributesErrors.type}</div>) : null}
                        </Col>

                        <Col sm="6" xs="12">
                            <strong className="mt-2">Modello</strong>
                            <InputText
                                disabled={readOnly ? true : false}
                                className={attributesErrors.model ? "p-error" : ""}
                                value={attributes.model || ""}
                                style={{ width: "100%" }}
                                onChange={e =>
                                    onAttributesChange(
                                        { model: e.target.value },
                                        attributes,
                                        containerHandleChange
                                    )
                                }
                            />
                            {attributesErrors.model ? (<div className="invalid-feedback">{attributesErrors.model}</div>) : null}
                        </Col>

                        <Col sm="6" xs="12">
                            <strong className="mt-2">Durata</strong>
                            <InputText
                                disabled={readOnly ? true : false}
                                className={attributesErrors.duration ? "p-error" : ""}
                                value={attributes.duration || ""}
                                style={{ width: "100%" }}
                                keyfilter="pint"
                                onChange={e =>
                                    onAttributesChange(
                                        { duration: e.target.value },
                                        attributes,
                                        containerHandleChange
                                    )
                                }
                            />
                            {attributesErrors.duration ? (<div className="invalid-feedback">{attributesErrors.duration}</div>) : null}
                        </Col>
                        <Col sm="6" xs="12">
                            <strong className="mt-2">Richiede Veicolo</strong>
                            <Dropdown
                                disabled={readOnly ? true : false}
                                filter
                                filterBy="label"
                                id="needsAuto"
                                className={attributesErrors.needsAuto ? "p-error" : ""}
                                value={attributes.needsAuto.toString() === "true" ? true : false}
                                showClear={true}
                                style={{ width: "100%" }}
                                options={[
                                    { label: "si", value: true },
                                    { label: "no", value: false }
                                ]}
                                onChange={e =>
                                    onAttributesChange({ needsAuto: e.value }, attributes, containerHandleChange)
                                }
                            />
                        </Col>
                        <Can
                            roles={[roles.ADMIN]}
                            yes={() => (
                                <React.Fragment>
                                    <Col sm="6" xs="12">
                                        <strong className="mt-2">Prezzo</strong>
                                        <InputText
                                            className={attributesErrors.price ? "p-error" : ""}
                                            value={attributes.price || ""}
                                            style={{ width: "100%" }}
                                            keyfilter="pint"
                                            onChange={e =>
                                                onAttributesChange(
                                                    { price: e.target.value },
                                                    attributes,
                                                    containerHandleChange
                                                )
                                            }
                                        />
                                        {attributesErrors.price ? (<div className="invalid-feedback">{attributesErrors.price}</div>) : null}
                                    </Col>
                                    <Col sm="6" xs="12">
                                        <strong className="mt-2">Quota Agente</strong>
                                        <InputText
                                            className={attributesErrors.quotaAgent ? "p-error" : ""}
                                            value={attributes.quotaAgent || ""}
                                            style={{ width: "100%" }}
                                            keyfilter="pint"
                                            onChange={e =>
                                                onAttributesChange(
                                                    { quotaAgent: e.target.value },
                                                    attributes,
                                                    containerHandleChange
                                                )
                                            }
                                        />
                                        {attributesErrors.quotaAgent ? (<div className="invalid-feedback">{attributesErrors.quotaAgent}</div>) : null}
                                    </Col>
                                </React.Fragment>
                            )}
                            no={() => null}
                        />
                    </Row>
                );
            }}
            renderDealer={() =>
                tenant ? (
                    <React.Fragment>
                        <Card body className="rounded bg-secondary mb-2">
                            <Row>
                                <Col sm="4" xs="12">
                                    <strong>Dealer: </strong>
                                    <Label className="h5">{tenant.name}</Label>
                                </Col>
                                <Col sm="4" xs="12" md="3">
                                    <strong>Telefono: </strong>
                                    <Label className="h5">{tenant.phone}</Label>
                                </Col>
                                <Col sm="4" xs="12" md="3">
                                    <strong>Email: </strong>
                                    <Label className="h5">{tenant.email}</Label>
                                </Col>
                            </Row>
                        </Card>
                    </React.Fragment>
                ) : (
                        <Spinner style={{ width: "3rem", height: "3rem" }} type="grow" />
                    )
            }
        />
    );
};
export default ProductEditor;
